import React from 'react';
import { ShoppingCart, Search, X } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CustomerHeader = ({
    supplierData,
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    onOpenCart,
    productCategories = [],
    recentCategories = [],
    selectedCategory = 'All',
    setSelectedCategory,
    onOpenMenu,
    containerClassName = "w-full px-4 py-3"
}) => {
    const { totalItems, cart = [] } = useCart();
    const tabs = ['All', 'Frequent', 'Offers'];

    return (
        <div className="w-full">
            <div className="bg-white">
                <div className={containerClassName}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            {onOpenMenu && (
                                <button
                                    onClick={onOpenMenu}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer mr-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </button>
                            )}
                            <img
                                src="/logo.jpg"
                                alt="Logo"
                                className="w-10 h-10 rounded-lg object-contain"
                            />
                            <img
                                src="/name.jpg"
                                alt="Yaseero"
                                className="h-8 object-contain"
                            />
                        </div>
                        <button
                            onClick={onOpenCart}
                            className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                        >
                            <ShoppingCart className="w-6 h-6 text-gray-600" />
                            {totalItems > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                                    {totalItems}
                                </span>
                            )}
                        </button>
                    </div>

                    <div className="bg-blue-50 rounded-lg px-3 py-1.5 mb-2">
                        <h2 className="font-bold text-gray-800 text-base">
                            {supplierData?.businessName || 'Supplier'}
                        </h2>
                    </div>

                    {/* Search Bar */}
                    <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder={activeTab === 'Pending Orders' ? "Search by Order # or Status..." : "Search items by name..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-10 py-2.5 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                                aria-label="Clear search"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    {/* Category Tabs */}
                    {activeTab !== 'Pending Orders' && (
                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 items-center">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 rounded-full font-medium transition-colors whitespace-nowrap text-sm ${activeTab === tab
                                        ? 'bg-cyan-500 text-white'
                                        : 'bg-gray-100 text-gray-600'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Category Pills Row */}
                    {activeTab !== 'Pending Orders' && productCategories.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 pt-1.5 items-center border-t border-gray-100 mt-2">
                            <button
                                onClick={() => setSelectedCategory(selectedCategory === 'Selected' ? 'All' : 'Selected')}
                                className={`px-3.5 py-1.5 rounded-full font-bold transition-all whitespace-nowrap text-xs flex items-center gap-1 border ${
                                    selectedCategory === 'Selected'
                                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm shadow-emerald-100'
                                        : 'bg-emerald-50/50 text-emerald-700 border-emerald-100 hover:bg-emerald-100/50'
                                }`}
                            >
                                <ShoppingCart className="w-3.5 h-3.5" />
                                Selected ({cart.length})
                            </button>
                            <button
                                onClick={() => setSelectedCategory('All')}
                                className={`px-3.5 py-1.5 rounded-full font-bold transition-all whitespace-nowrap text-xs ${
                                    selectedCategory === 'All'
                                        ? 'bg-cyan-500 text-white shadow-sm shadow-cyan-100'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                All Categories
                            </button>
                            {recentCategories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-3.5 py-1.5 rounded-full font-bold transition-all whitespace-nowrap text-xs ${
                                        selectedCategory === cat
                                            ? 'bg-cyan-500 text-white shadow-sm shadow-cyan-100'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                            {productCategories.some(cat => !recentCategories.includes(cat)) && (
                                <div className="relative inline-flex items-center">
                                    <select
                                        value={productCategories.includes(selectedCategory) && !recentCategories.includes(selectedCategory) ? selectedCategory : ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "") {
                                                setSelectedCategory("All");
                                            } else {
                                                setSelectedCategory(val);
                                            }
                                        }}
                                        className={`px-3.5 py-1.5 rounded-full font-bold transition-all whitespace-nowrap text-xs border cursor-pointer outline-none appearance-none pr-7 ${
                                            productCategories.includes(selectedCategory) && !recentCategories.includes(selectedCategory)
                                                ? 'bg-cyan-500 text-white border-cyan-500 shadow-sm shadow-cyan-100'
                                                : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'
                                        } focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2`}
                                    >
                                        <option value="" className="text-gray-900 bg-white">More...</option>
                                        {productCategories
                                            .filter(cat => !recentCategories.includes(cat))
                                            .map(cat => (
                                                <option key={cat} value={cat} className="text-gray-900 bg-white">
                                                    {cat}
                                                </option>
                                            ))
                                        }
                                    </select>
                                    <span className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center ${
                                        productCategories.includes(selectedCategory) && !recentCategories.includes(selectedCategory) ? 'text-white' : 'text-gray-500'
                                    }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                        </svg>
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerHeader;
